import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

interface Shot {
  src: string;
  title: string;
  file: string;
  caption: string;
  tags: string[];
  accent: string;
}

// 2 n8n workflow screenshots (landscape). Files in /public/assets/workflows/
const n8nShots: Shot[] = [
  {
    src: '/assets/workflows/n8n1.png',
    title: 'Slack Task Bot',
    file: 'slack-tasks.n8n',
    caption:
      'A Slack Events webhook verifies the request, reads tasks from Google Sheets, finds the matching task, interprets the reply with logic, updates status, and posts a confirmation back in-thread.',
    tags: ['Slack Webhook', 'Google Sheets', 'IF / Filter', 'Reply in thread'],
    accent: '#22e6ff',
  },
  {
    src: '/assets/workflows/n8n2.png',
    title: 'Event API → GoHighLevel Funnel',
    file: 'event-api.n8n',
    caption:
      'A GET webhook validates an incoming event ID, looks it up in the events database, checks public availability, and returns clean event data to a GoHighLevel funnel — with 400 / 404 error handling.',
    tags: ['HTTP Webhook', 'Google Sheets', 'Routing', 'GoHighLevel'],
    accent: '#9d5cff',
  },
];

// 2 GoHighLevel workflow screenshots (portrait).
const ghlShots: Shot[] = [
  {
    src: '/assets/workflows/GHL1.png',
    title: 'GHL IVR Call Routing',
    file: 'ivr-routing.ghl',
    caption:
      'An inbound IVR greets the caller, collects keypad input, maps it, then routes to connect a call, capture a voicemail, or hand off to an AI voice agent.',
    tags: ['IVR', 'Call routing', 'AI Voice Agent'],
    accent: '#22e6ff',
  },
  {
    src: '/assets/workflows/GHL2.png',
    title: 'Missed-Call Speed-to-Lead',
    file: 'speed-to-lead.ghl',
    caption:
      'When a call is missed, a short delay fires a natural-feeling SMS to the lead, assigns the contact, tags it, and alerts the rep by push notification and internal SMS.',
    tags: ['Missed call', 'SMS text-back', 'Auto-assign'],
    accent: '#2bffb0',
  },
];

/** Image that falls back to a styled "slot" placeholder if the file is missing. */
const ShotImage: React.FC<{
  shot: Shot;
  className?: string;
  onOpen?: () => void;
}> = ({ shot, className = '', onOpen }) => {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <div
        className={`flex min-h-[220px] w-full flex-col items-center justify-center gap-2 border border-dashed border-canvas-border bg-canvas-deep/60 text-center ${className}`}
      >
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="#6f76a3" strokeWidth="1.4" />
          <path d="M3 16l5-4 4 3 3-3 6 5" stroke="#6f76a3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="9" cy="9.5" r="1.4" fill="#6f76a3" />
        </svg>
        <span className="font-mono text-[11px] text-ink-muted">{shot.file}</span>
        <span className="px-4 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
          screenshot not found
        </span>
      </div>
    );
  }

  return (
    <img
      src={shot.src}
      alt={shot.title}
      loading="lazy"
      onClick={onOpen}
      onError={() => setErrored(true)}
      className={`cursor-zoom-in ${className}`}
    />
  );
};

/** A node-framed window around a screenshot (the "editor" chrome). */
const WorkflowFrame: React.FC<{ shot: Shot; onOpen: () => void }> = ({
  shot,
  onOpen,
}) => (
  <div className="n8n-node group overflow-hidden p-0">
    <div className="flex items-center justify-between border-b border-canvas-border/70 px-4 py-2.5">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-neon-magenta/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-neon-amber/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-neon-green/70" />
        <span className="ml-2 font-mono text-xs text-ink-muted">{shot.file}</span>
      </div>
      <span
        className="rounded px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider"
        style={{ color: shot.accent, border: `1px solid ${shot.accent}55` }}
      >
        live
      </span>
    </div>

    <div className="relative w-full overflow-hidden bg-canvas-deep">
      <ShotImage
        shot={shot}
        onOpen={onOpen}
        className="block h-auto w-full transition-transform duration-500 group-hover:scale-[1.02]"
      />
      <button
        onClick={onOpen}
        className="absolute right-3 top-3 rounded-md bg-canvas-base/80 px-2 py-1 font-mono text-[10px] text-neon-cyan opacity-0 backdrop-blur transition-opacity group-hover:opacity-100"
      >
        ⤢ expand
      </button>
    </div>

    <div className="p-4">
      <h3 className="font-display text-base font-semibold text-ink-main">
        {shot.title}
      </h3>
      <p className="mt-1 text-sm text-ink-soft">{shot.caption}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {shot.tags.map((t) => (
          <span key={t} className="tech-chip !py-1 !text-[11px]">
            {t}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const WorkflowsSection: React.FC = () => {
  const { themeConfig } = useTheme();
  const [lightbox, setLightbox] = useState<Shot | null>(null);

  return (
    <section id="workflows" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-12 max-w-2xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="h-px w-6 bg-neon-magenta/60" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-magenta">
            {'// proof of work'}
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
          Workflows I&apos;ve <span className="gradient-text">shipped</span>
        </h2>
        <p className="mt-3 text-lg text-ink-soft">
          Real automations from the n8n editor and GoHighLevel — the kind of
          systems I&apos;ll build for you. Click any screen to zoom in.
        </p>
      </motion.div>

      {/* n8n landscape — stacked full-width so the whole flow is readable */}
      <div className="mb-6 flex items-center gap-2">
        <span className="font-mono text-xs uppercase tracking-widest text-neon-cyan">
          n8n workflows
        </span>
        <span className="h-px flex-1 bg-canvas-border/60" />
      </div>

      <div className="mb-16 space-y-6">
        {n8nShots.map((shot) => (
          <motion.div
            key={shot.file}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <WorkflowFrame shot={shot} onOpen={() => setLightbox(shot)} />
          </motion.div>
        ))}
      </div>

      {/* GHL portrait — two-up */}
      <div className="mb-6 flex items-center gap-2">
        <span className="font-mono text-xs uppercase tracking-widest text-neon-green">
          gohighlevel workflows
        </span>
        <span className="h-px flex-1 bg-canvas-border/60" />
      </div>

      <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2">
        {ghlShots.map((shot) => (
          <motion.div
            key={shot.file}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <WorkflowFrame shot={shot} onOpen={() => setLightbox(shot)} />
          </motion.div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-canvas-base/90 p-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <motion.div
              className="relative max-h-[90vh] max-w-5xl overflow-hidden rounded-xl border border-neon-cyan/40 shadow-neon-cyan"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-canvas-border bg-canvas-node px-4 py-2.5">
                <span className="font-mono text-xs text-ink-soft">{lightbox.file}</span>
                <button
                  onClick={() => setLightbox(null)}
                  className="font-mono text-xs text-ink-muted hover:text-neon-magenta"
                >
                  ✕ close
                </button>
              </div>
              <ShotImage
                shot={lightbox}
                className="max-h-[78vh] w-full bg-canvas-deep object-contain"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default WorkflowsSection;
